import { z } from 'zod';
import { crowdexAgentMcpServerSchema, executionTurnCheckpointSchema } from '../request-schemas';

/** Core-owned contract implemented by privileged remote execution providers. */
export const EXECUTION_HOST_PROVIDER_CONTRACT = 'execution-host.provider.v1' as const;
export const DEFAULT_EXECUTION_PROVIDER_ID = 'crowdex-cloud' as const;
export const DEFAULT_EXECUTION_PROFILE_ID = 'standard' as const;

export const executionProviderCapabilitiesSchema = z
  .object({
    interactiveTerminal: z.boolean(),
    workspaceCheckpoints: z.boolean(),
    incrementalCheckpoints: z.boolean(),
    suspendResume: z.boolean(),
    publicPorts: z.boolean(),
    privateNetworking: z.boolean(),
    persistentVolume: z.boolean(),
    architectures: z.array(z.enum(['amd64', 'arm64'])).min(1),
  })
  .strict();
export type ExecutionProviderCapabilities = z.infer<typeof executionProviderCapabilitiesSchema>;

export const executionProfileSchema = z
  .object({
    id: z.string().regex(/^[a-z0-9][a-z0-9._-]*$/),
    name: z.string().min(1).max(160),
    description: z.string().max(2000).optional(),
    region: z.string().min(1).max(128).optional(),
    architecture: z.enum(['amd64', 'arm64']).optional(),
    resources: z
      .object({
        cpu: z.number().positive().optional(),
        memoryMiB: z.number().int().positive().optional(),
        diskGiB: z.number().int().positive().optional(),
        gpu: z.string().min(1).max(128).optional(),
      })
      .strict()
      .optional(),
  })
  .strict();
export type ExecutionProfile = z.infer<typeof executionProfileSchema>;

export const executionProviderDescriptorSchema = z
  .object({
    id: z.string().regex(/^[a-z0-9][a-z0-9._-]*$/),
    name: z.string().min(1).max(160),
    description: z.string().max(2000).optional(),
    capabilities: executionProviderCapabilitiesSchema,
    profiles: z.array(executionProfileSchema).min(1).max(128),
  })
  .strict();
export type ExecutionProviderDescriptor = z.infer<typeof executionProviderDescriptorSchema>;

/** Placement is independent from the ACP or DAG runtime attached to a session. */
export const executionPlacementSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('local') }).strict(),
  z
    .object({
      kind: z.literal('worktree'),
      worktreeId: z.string().min(1).optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('remote'),
      providerId: z.string().regex(/^[a-z0-9][a-z0-9._-]*$/),
      profileId: z.string().regex(/^[a-z0-9][a-z0-9._-]*$/),
    })
    .strict(),
]);
export type ExecutionPlacement = z.infer<typeof executionPlacementSchema>;

export const remoteExecutionRuntimeSchema = z
  .object({
    id: z.string().min(1),
    providerId: z.string().regex(/^[a-z0-9][a-z0-9._-]*$/),
    profileId: z.string().regex(/^[a-z0-9][a-z0-9._-]*$/),
    name: z.string().min(1),
    url: z.string().url(),
    agentUrl: z.string().url(),
    providerValues: z.record(z.string(), z.string()).default({}),
    token: z.string().min(1),
    workspaceRoot: z.string().min(1),
    workspaceIdentity: z.string().min(1),
    agentId: z.string().min(1),
    mcpServers: z.array(crowdexAgentMcpServerSchema).max(128).optional(),
    sandboxName: z.string().min(1).optional(),
    created: z.boolean().default(false),
    resumed: z.boolean().default(false),
    suspendAt: z.string().datetime().optional(),
    expiresAt: z.string().datetime().optional(),
  })
  .passthrough();
export type RemoteExecutionRuntime = z.infer<typeof remoteExecutionRuntimeSchema>;

export type EnsureExecutionRuntimeInput = {
  userId: string;
  taskId: string;
  agentId?: string;
  profileId: string;
  onProgress?: (phase: string) => void;
};

/** Privileged server-side provider. Credentials and control-plane tokens never reach renderers. */
export interface ExecutionHostProviderV1 {
  descriptor(): Promise<ExecutionProviderDescriptor> | ExecutionProviderDescriptor;
  ensureRuntime(input: EnsureExecutionRuntimeInput): Promise<RemoteExecutionRuntime | null>;
  terminate?(input: { userId: string; taskId: string }): Promise<void>;
}

// ---------------------------------------------------------------------------
// Execution API: what an execution reports to its environment (never its database).
// ---------------------------------------------------------------------------

/** Checkpoints of a turn's workspace: `?baseRef=&baseCommitSha=&kind=`, the content as body. */
export const executionCheckpointQuerySchema = executionTurnCheckpointSchema;
export type ExecutionCheckpointQuery = z.infer<typeof executionCheckpointQuerySchema>;

export const executionCheckpointResponseSchema = z
  .object({ revision: z.number().int().nonnegative(), stored: z.boolean() })
  .strict();

/** `PUT /api/executions/:taskId/agent-session` and its `GET`. */
export const executionAgentSessionSchema = z
  .object({ agentId: z.string().min(1).max(256), session: z.unknown() })
  .strict();
export type ExecutionAgentSession = z.infer<typeof executionAgentSessionSchema>;

/** `POST /api/executions/:taskId/heartbeat`, every 30 seconds. */
export const executionHeartbeatSchema = z
  .object({
    state: z.enum(['ready', 'busy', 'idle']),
    runtimeVersion: z.string().min(1).max(256),
    startedAt: z.iso.datetime(),
  })
  .strict();
export type ExecutionHeartbeat = z.infer<typeof executionHeartbeatSchema>;

export { executionTurnCheckpointSchema };
export type ExecutionTurnCheckpoint = z.infer<typeof executionTurnCheckpointSchema>;
